import * as React from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { blue, neutral, pallette } from '../theme/colors';
import clsx from 'clsx';
import { fontFamilies } from '../theme/text';

export interface PrimaryButtonProps {
  /** Text of the button */
  text: string;
  /** Title of the button, shown on hover */
  title: string;
  /** Set to `true` to disable the button */
  deactive?: boolean;
  /** extra styles to be included inline */
  styles?: any;
  /** `onClick` handler */
  handleClick: (event?: Event) => void;
}

/**
 * baseStyles
 * make base styles object for primary and secondary buttons
 */
export const baseButtonStyles = makeStyles({
  root: {
    padding: '0 18px',
    borderRadius: '2px',
    minHeight: '30px',
    whiteSpace: 'nowrap',
    fontFamily: fontFamilies.heading,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'inherit',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: pallette.textLight
  }
});

const useStyles = makeStyles({
  root: {
    minWidth: '200px',
    backgroundColor: pallette.backgroundDark,
    '&.Mui-disabled': {
      backgroundColor: neutral[250],
      color: neutral[600]
    },
    '&:hover': {
      backgroundColor: blue[600]
    },
    '&:focus': {
      backgroundColor: blue[300],
      color: pallette.textDark
    }
  }
});

/**
 * Primary Button:
 * The highest priority button, generally used for final submit buttons in views with user input.
 * Extends Material UI Button, see https://material-ui.com/api/button/
 */
const PrimaryButton = ({ text, title, deactive, styles, handleClick }: PrimaryButtonProps) => {
  const base = baseButtonStyles();
  const classes = useStyles();
  return (
    <Button
      className={clsx(base.root, classes.root)}
      type="submit"
      title={title}
      onClick={(event) => {
        event.preventDefault();
        handleClick();
      }}
      variant="contained"
      disabled={deactive}
      style={styles}
    >
      {text}
    </Button>
  );
};

export default PrimaryButton;
